import { useState } from "react";
import { Button, Divider } from "../../../../components";
import { useLoanApplication } from "../../../../contexts";
import { useMedia } from "../../../../hooks";
import {
  LoanProductType,
  MediaPurpose,
  MediaType,
} from "../../../../hooks/api/types";
import { PlusIcon } from "../../../../assets/icons";
import { AddDocumentModal } from "../../FinancialInformation";
import AttachedDocumentCard from "./AttachedDocumentCard";
import { useDpConfig } from "../../../../contexts/dpConfig";
import { useLoanProductConfigFlags } from "../../../../hooks/useLoanProductConfigFlags";

const DocumentsReviewSection = () => {
  const [bankStatementsUploading, setBankStatementsUploading] = useState<
    boolean[]
  >([]);
  const [salesStatementUploading, setSalesStatementUploading] = useState<
    boolean[]
  >([]);
  const [posSalesStatementUploading, setPosSalesStatementUploading] = useState<
    boolean[]
  >([]);
  const [salesInvoiceUploading, setSalesInvoiceUploading] = useState<boolean[]>(
    []
  );
  const [tradeLicenseUploading, setTradeLicenseUploading] = useState<boolean[]>(
    []
  );
  const [moaUploading, setMoaUploading] = useState<boolean[]>([]);
  const [poaUploading, setPoaUploading] = useState<boolean[]>([]);
  const [vatStatementsUploading, setVatStatementsUploading] = useState<
    boolean[]
  >([]);
  const [vatCertificatesUploading, setVatCertificatesUploading] = useState<
    boolean[]
  >([]);
  const [genericDocUploading, setGenericDocUploading] = useState<boolean[]>([]);
  const loanApplicationData = useLoanApplication();
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const {
    state: { uiLocalizationData },
  } = useDpConfig();

  const {
    loanType,
    fixedTermLoan,
    revenueBasedLoan,
    posRevenueBasedLoan,
    buyNowPayLaterLoan,
  } = loanApplicationData.state;

  const getProductSpecificLoanAmount = (): number | undefined => {
    if (loanType === LoanProductType.FIXED_TERM) {
      return fixedTermLoan?.amount;
    } else if (loanType === LoanProductType.REVENUE_BASED_FINANCING) {
      return revenueBasedLoan?.amount;
    } else if (loanType === LoanProductType.POS_REVENUE_BASED_FINANCING) {
      return posRevenueBasedLoan?.amount;
    } else if (loanType === LoanProductType.BNPL) {
      return buyNowPayLaterLoan?.amount;
    }
    return undefined;
  };

  const productSpecificLoanAmount = getProductSpecificLoanAmount();

  const {
    disableBankStatementsUpload,
    disableVATStatementsUpload,
    disableSalesStatementsUpload,
    disablePosSalesStatementsUpload,
    disableSalesInvoiceUpload,
  } = useLoanProductConfigFlags(loanType, productSpecificLoanAmount);

  const { upload } = useMedia();

  return (
    <div className="cx-bg-white cx-rounded-lg cx-w-full cx-shadow-md cx-p-5 md:cx-p-10 cx-min-w-[343px] cx-mt-10">
      <div className="cx-flex cx-flex-row cx-place-content-between max-md:cx-justify-between max-md:cx-items-center cx-w-full">
        <div className="cx-text-2xl cx-font-bold">Attached Documents</div>
        <Button
          label={
            <div className="cx-flex cx-items-center">
              <PlusIcon className="cx-mr-2" />
              <div>Add document</div>
            </div>
          }
          slim={true}
          onClick={() => {
            setAddDocumentModalOpen(true);
          }}
        />
      </div>

      {/* <div className="cx-mt-6">
        <div className="cx-text-lg cx-font-semibold">Emirates ID</div>
        <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
          <AttachedDocumentCard document={{ label: "Front" }} />
          <AttachedDocumentCard document={{ label: "Back" }} />
        </div>
        <Divider />
      </div> */}

      {!!loanApplicationData.state.docBankStatements.filter(Boolean).length &&
        !disableBankStatementsUpload && (
          <div className="cx-mt-6">
            <div className="cx-text-lg cx-font-semibold">Bank Statements</div>
            <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
              {loanApplicationData.state.docBankStatements
                .filter(Boolean)
                .map((doc, idx) => (
                  <AttachedDocumentCard
                    key={doc}
                    document={{ id: doc }}
                    loading={bankStatementsUploading[idx]}
                    showTitle={true}
                    onChange={async (file) => {
                      if (!file) return;
                      setBankStatementsUploading((prev) => {
                        const newPrev = [...prev];
                        newPrev[idx] = true;
                        return newPrev;
                      });
                      const id = await upload({
                        file,
                        type: MediaType.DOCUMENT,
                        purpose: MediaPurpose.BANK_STATEMENT,
                        loanApplicationId: loanApplicationData.state.id,
                      });

                      const newStatements = [
                        ...loanApplicationData.state.docBankStatements,
                      ];
                      newStatements[idx] = id;

                      await loanApplicationData.actions
                        .update({
                          docBankStatements: newStatements,
                        })
                        .catch((err) => {
                          console.log(err);
                        })
                        .finally(() => {
                          setBankStatementsUploading((prev) => {
                            const newPrev = [...prev];
                            newPrev[idx] = false;
                            return newPrev;
                          });
                        });
                    }}
                  />
                ))}
            </div>
            <Divider />
          </div>
        )}

      {/* Business information: Trade License, MOA, POA in a single flex row with type label per card */}
      {(!!loanApplicationData.state.docTradeLicenses?.length ||
        !!loanApplicationData.state.docMOAs?.length ||
        !!loanApplicationData.state.docPOAs?.length) && (
        <div className="cx-mt-6">
          <div className="cx-text-lg cx-font-semibold cx-mb-4">
            Business information
          </div>
          <div className="cx-mb-4 cx-flex cx-flex-row cx-flex-wrap cx-justify-start cx-items-start cx-gap-4">
            {loanApplicationData.state.docTradeLicenses?.map((doc, idx) => (
              <div key={doc} className="cx-flex cx-flex-col">
                <div className="cx-text-sm cx-font-medium cx-mb-1 cx-text-text-primary">
                  Trade license
                </div>
                <AttachedDocumentCard
                  document={{ id: doc }}
                  loading={tradeLicenseUploading[idx]}
                  showTitle={false}
                  onChange={async (file) => {
                    if (!file) return;
                    setTradeLicenseUploading((prev) => {
                      const newPrev = [...prev];
                      newPrev[idx] = true;
                      return newPrev;
                    });
                    const id = await upload({
                      file,
                      type: MediaType.DOCUMENT,
                      purpose: MediaPurpose.TRADE_LICENSE,
                      loanApplicationId: loanApplicationData.state.id,
                    });
                    const newStatements = [
                      ...loanApplicationData.state.docTradeLicenses,
                    ];
                    newStatements[idx] = id;
                    await loanApplicationData.actions
                      .update({ docTradeLicenses: newStatements })
                      .catch((err) => console.log(err))
                      .finally(() => {
                        setTradeLicenseUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = false;
                          return newPrev;
                        });
                      });
                  }}
                />
              </div>
            ))}
            {loanApplicationData.state.docMOAs?.map((doc, idx) => (
              <div key={doc} className="cx-flex cx-flex-col">
                <div className="cx-text-sm cx-font-medium cx-mb-1 cx-text-text-primary">
                  Memorandum of Association
                </div>
                <AttachedDocumentCard
                  document={{ id: doc }}
                  loading={moaUploading[idx]}
                  showTitle={false}
                  onChange={async (file) => {
                    if (!file) return;
                    setMoaUploading((prev) => {
                      const newPrev = [...prev];
                      newPrev[idx] = true;
                      return newPrev;
                    });
                    const id = await upload({
                      file,
                      type: MediaType.DOCUMENT,
                      purpose: MediaPurpose.MEMORANDUM_OF_ASSOCIATION,
                      loanApplicationId: loanApplicationData.state.id,
                    });
                    const newStatements = [
                      ...loanApplicationData.state.docMOAs,
                    ];
                    newStatements[idx] = id;
                    await loanApplicationData.actions
                      .update({ docMOAs: newStatements })
                      .catch((err) => console.log(err))
                      .finally(() => {
                        setMoaUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = false;
                          return newPrev;
                        });
                      });
                  }}
                />
              </div>
            ))}
            {loanApplicationData.state.docPOAs?.map((doc, idx) => (
              <div key={doc} className="cx-flex cx-flex-col">
                <div className="cx-text-sm cx-font-medium cx-mb-1 cx-text-text-primary">
                  Power of Attorney
                </div>
                <AttachedDocumentCard
                  document={{ id: doc }}
                  loading={poaUploading[idx]}
                  showTitle={false}
                  onChange={async (file) => {
                    if (!file) return;
                    setPoaUploading((prev) => {
                      const newPrev = [...prev];
                      newPrev[idx] = true;
                      return newPrev;
                    });
                    const id = await upload({
                      file,
                      type: MediaType.DOCUMENT,
                      purpose: MediaPurpose.POWER_OF_ATTORNEY,
                      loanApplicationId: loanApplicationData.state.id,
                    });
                    const newStatements = [
                      ...loanApplicationData.state.docPOAs,
                    ];
                    newStatements[idx] = id;
                    await loanApplicationData.actions
                      .update({ docPOAs: newStatements })
                      .catch((err) => console.log(err))
                      .finally(() => {
                        setPoaUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = false;
                          return newPrev;
                        });
                      });
                  }}
                />
              </div>
            ))}
          </div>
          <Divider />
        </div>
      )}

      <div className="cx-mt-6">
        {loanApplicationData.state.loanType ===
          LoanProductType.REVENUE_BASED_FINANCING &&
          loanApplicationData?.state?.revenueBasedLoan?.salesStatements
            ?.length > 0 &&
          !disableSalesStatementsUpload && (
            <div className="cx-mt-6">
              <div className="cx-text-lg cx-font-semibold">Sales Statement</div>
              <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
                {loanApplicationData.state.revenueBasedLoan.salesStatements.map(
                  (doc, idx) => (
                    <AttachedDocumentCard
                      key={doc.documentId}
                      document={{ id: doc.documentId }}
                      loading={salesStatementUploading[idx]}
                      showTitle={false}
                      onChange={async (file) => {
                        if (!file) return;
                        setSalesStatementUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = true;
                          return newPrev;
                        });
                        const id = await upload({
                          file,
                          type: MediaType.DOCUMENT,
                          purpose: MediaPurpose.SALES_STATEMENT,
                          loanApplicationId: loanApplicationData.state.id,
                        });

                        const newStatements = [
                          ...loanApplicationData.state.revenueBasedLoan
                            .salesStatements,
                        ];
                        newStatements[idx].documentId = id;

                        await loanApplicationData.actions
                          .update({
                            revenueBasedLoan: {
                              ...loanApplicationData.state.revenueBasedLoan,
                              salesStatements: newStatements,
                            },
                          })
                          .catch((err) => {
                            console.log(err);
                          })
                          .finally(() => {
                            setSalesStatementUploading((prev) => {
                              const newPrev = [...prev];
                              newPrev[idx] = false;
                              return newPrev;
                            });
                          });
                      }}
                    />
                  )
                )}
              </div>
              <Divider />
            </div>
          )}
      </div>

      <div className="cx-mt-6">
        {loanApplicationData.state.loanType ===
          LoanProductType.POS_REVENUE_BASED_FINANCING &&
          loanApplicationData?.state?.posRevenueBasedLoan?.posSalesStatements
            ?.length > 0 &&
          !disablePosSalesStatementsUpload && (
            <div className="cx-mt-6">
              <div className="cx-text-lg cx-font-semibold">
                POS Sales Statement
              </div>
              <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
                {loanApplicationData.state.posRevenueBasedLoan.posSalesStatements.map(
                  (doc, idx) => (
                    <AttachedDocumentCard
                      key={doc.posStatementId}
                      document={{ id: doc.posStatementId }}
                      loading={posSalesStatementUploading[idx]}
                      showTitle={false}
                      onChange={async (file) => {
                        if (!file) return;
                        setPosSalesStatementUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = true;
                          return newPrev;
                        });
                        const id = await upload({
                          file,
                          type: MediaType.DOCUMENT,
                          purpose: MediaPurpose.POS_SALES_STATEMENT,
                          loanApplicationId: loanApplicationData.state.id,
                        });

                        const newStatements = [
                          ...loanApplicationData.state.posRevenueBasedLoan
                            .posSalesStatements,
                        ];
                        newStatements[idx].posStatementId = id;

                        await loanApplicationData.actions
                          .update({
                            posRevenueBasedLoan: {
                              ...loanApplicationData.state.posRevenueBasedLoan,
                              posSalesStatements: newStatements,
                            },
                          })
                          .catch((err) => {
                            console.log(err);
                          })
                          .finally(() => {
                            setPosSalesStatementUploading((prev) => {
                              const newPrev = [...prev];
                              newPrev[idx] = false;
                              return newPrev;
                            });
                          });
                      }}
                    />
                  )
                )}
              </div>
              <Divider />
            </div>
          )}
      </div>

      <div className="cx-mt-6">
        {loanApplicationData.state.loanType === LoanProductType.BNPL &&
          loanApplicationData?.state?.buyNowPayLaterLoan?.salesInvoices
            ?.length &&
          !disableSalesInvoiceUpload && (
            <div className="cx-mt-6">
              <div className="cx-text-lg cx-font-semibold">
                {uiLocalizationData.reviewScreen.invoiceLabel}
              </div>
              <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
                {loanApplicationData.state.buyNowPayLaterLoan.salesInvoices.map(
                  (doc, idx) => (
                    <AttachedDocumentCard
                      key={doc.invoiceId}
                      document={{ id: doc.invoiceId }}
                      loading={salesInvoiceUploading[idx]}
                      showTitle={false}
                      onChange={async (file) => {
                        if (!file) return;
                        setSalesInvoiceUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = true;
                          return newPrev;
                        });
                        const id = await upload({
                          file,
                          type: MediaType.DOCUMENT,
                          purpose: MediaPurpose.INVOICE,
                          loanApplicationId: loanApplicationData.state.id,
                        });

                        const newInvoices = [
                          ...loanApplicationData.state.buyNowPayLaterLoan
                            .salesInvoices,
                        ];
                        newInvoices[idx].invoiceId = id;

                        await loanApplicationData.actions
                          .update({
                            buyNowPayLaterLoan: {
                              ...loanApplicationData.state.buyNowPayLaterLoan,
                              salesInvoices: newInvoices,
                            },
                          })
                          .catch((err) => {
                            console.log(err);
                          })
                          .finally(() => {
                            setSalesInvoiceUploading((prev) => {
                              const newPrev = [...prev];
                              newPrev[idx] = false;
                              return newPrev;
                            });
                          });
                      }}
                    />
                  )
                )}
              </div>
              <Divider />
            </div>
          )}
      </div>
      {!!loanApplicationData.state.docVatStatements?.length &&
        !disableVATStatementsUpload && (
          <div className="cx-mt-6">
            <div className="cx-text-lg cx-font-semibold">VAT Statements</div>
            <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
              {loanApplicationData.state.docVatStatements.map((doc, idx) => (
                <AttachedDocumentCard
                  key={doc}
                  document={{
                    id: doc,
                  }}
                  loading={vatStatementsUploading[idx]}
                  showTitle={true}
                  onChange={async (file) => {
                    if (!file) return;
                    setVatStatementsUploading((prev) => {
                      const newPrev = [...prev];
                      newPrev[idx] = true;
                      return newPrev;
                    });

                    const id = await upload({
                      file,
                      type: MediaType.DOCUMENT,
                      purpose: MediaPurpose.VAT_STATEMENT,
                      loanApplicationId: loanApplicationData.state.id,
                    });

                    const newStatements = [
                      ...loanApplicationData.state.docVatStatements,
                    ];
                    newStatements[idx] = id;

                    await loanApplicationData.actions
                      .update({
                        docVatStatements: newStatements,
                      })
                      .catch((err) => {
                        console.log(err);
                      })
                      .finally(() => {
                        setVatStatementsUploading((prev) => {
                          const newPrev = [...prev];
                          newPrev[idx] = false;
                          return newPrev;
                        });
                      });
                  }}
                />
              ))}
            </div>
            {!!loanApplicationData.state.docGeneric?.length && <Divider />}
          </div>
        )}

      {!!loanApplicationData.state.docVatCertificates?.length &&
        !disableVATStatementsUpload && (
          <div className="cx-mt-6">
            <div className="cx-text-lg cx-font-semibold">VAT Certificate</div>
            <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
              {loanApplicationData.state.docVatCertificates.map((doc, idx) => (
                <AttachedDocumentCard
                  key={doc}
                  document={{ id: doc }}
                  loading={vatCertificatesUploading[idx]}
                  showTitle={true}
                  onChange={async (file) => {
                    if (!file) return;
                    setVatCertificatesUploading((prev) => {
                      const next = [...prev];
                      next[idx] = true;
                      return next;
                    });
                    const id = await upload({
                      file,
                      type: MediaType.DOCUMENT,
                      purpose: MediaPurpose.VAT_CERTIFICATE,
                      loanApplicationId: loanApplicationData.state.id,
                    });
                    const newCertificates = [
                      ...loanApplicationData.state.docVatCertificates,
                    ];
                    newCertificates[idx] = id;
                    await loanApplicationData.actions
                      .update({ docVatCertificates: newCertificates })
                      .finally(() => {
                        setVatCertificatesUploading((prev) => {
                          const next = [...prev];
                          next[idx] = false;
                          return next;
                        });
                      });
                  }}
                />
              ))}
            </div>
            <Divider />
          </div>
        )}

      {!!loanApplicationData.state.docGeneric?.length && (
        <div className="cx-mt-6">
          <div className="cx-text-lg cx-font-semibold">
            Additional Financial Documents
          </div>
          <div className="cx-mb-6 cx-mt-4 cx-flex cx-flex-row cx-flex-wrap cx-gap-4">
            {loanApplicationData.state.docGeneric.map((doc, idx) => (
              <AttachedDocumentCard
                key={doc}
                document={{
                  id: doc,
                }}
                accept="*"
                loading={genericDocUploading[idx]}
                showTitle={true}
                onChange={async (file) => {
                  if (!file) return;

                  setGenericDocUploading((prev) => {
                    const newPrev = [...prev];
                    newPrev[idx] = true;
                    return newPrev;
                  });

                  const id = await upload({
                    file,
                    type: MediaType.DOCUMENT,
                    purpose: MediaPurpose.GENERIC_DOCUMENT,
                    loanApplicationId: loanApplicationData.state.id,
                  });

                  const newGenericDocumentsList = [
                    ...loanApplicationData.state.docGeneric,
                  ];
                  newGenericDocumentsList[idx] = id;

                  await loanApplicationData.actions
                    .update({
                      docGeneric: newGenericDocumentsList,
                    })
                    .catch((err) => {
                      console.log(err);
                    })
                    .finally(() => {
                      setGenericDocUploading((prev) => {
                        const newPrev = [...prev];
                        newPrev[idx] = false;
                        return newPrev;
                      });
                    });
                }}
              />
            ))}
          </div>
        </div>
      )}
      <AddDocumentModal
        open={addDocumentModalOpen}
        onClose={() => setAddDocumentModalOpen(false)}
      />
    </div>
  );
};

export default DocumentsReviewSection;
